import React, { useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { ContextMenu } from "primereact/contextmenu";
import { isFeatureEnabled } from "../../data/Services/settings";
import { symbolSpotlightRef } from "../Navbar/symbolSpotlightRef";
import { clampMenuToViewport } from "../ChartWrapper/components/chartContextMenuEvent.js";
import useWorkspaceStore from "../../store/workspaceStore";

// Context menu for symbol-capable NON-chart panels (watchlist / book): one
// action that opens the shared SymbolSpotlight, focused on the panel so the
// spotlight routes the pick correctly (grid → add to watchlist, bookByPrice →
// change the book symbol). Available whenever the chartContextMenu release
// flag is on — INDEPENDENT of the extended-menu setting: on touch the panel
// itself has no other affordance.
//
// Trigger: native `contextmenu` (desktop right-click; Android long-press
// fires it natively) + a synthesized long-press for iOS Safari, which never
// dispatches contextmenu. After a synthesized show, the next click is
// swallowed so the touchend does not instantly close the menu.
const LONG_PRESS_MS = 600;
const MOVE_TOLERANCE = 12;

const PanelSymbolContextMenu = ({ targetRef, panelType, label, icon }) => {
  const cmRef = useRef(null);

  useEffect(() => {
    if (!isFeatureEnabled("chartContextMenu")) return undefined;
    const el = targetRef.current;
    if (!el) return undefined;

    const show = (x, y) => {
      cmRef.current?.show({
        pageX: x,
        pageY: y,
        clientX: x,
        clientY: y,
        preventDefault: () => {},
        stopPropagation: () => {},
      });
    };

    let timer = null;
    let sx = 0;
    let sy = 0;
    const cancel = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const swallowNextClick = () => {
      const h = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };
      document.addEventListener("click", h, { capture: true, once: true });
      setTimeout(
        () => document.removeEventListener("click", h, { capture: true }),
        500,
      );
    };

    const onContextMenu = (e) => {
      cancel(); // Android long-press fires contextmenu natively — no double show
      e.preventDefault();
      show(e.clientX, e.clientY);
    };

    const onTouchStart = (e) => {
      cancel();
      if (e.touches?.length !== 1) return;
      sx = e.touches[0].clientX;
      sy = e.touches[0].clientY;
      timer = setTimeout(() => {
        timer = null;
        swallowNextClick();
        show(sx, sy);
      }, LONG_PRESS_MS);
    };

    const onTouchMove = (e) => {
      const t = e.touches?.[0];
      if (
        !t ||
        Math.abs(t.clientX - sx) > MOVE_TOLERANCE ||
        Math.abs(t.clientY - sy) > MOVE_TOLERANCE
      ) {
        cancel(); // scroll/drag — not a long-press
      }
    };

    el.addEventListener("contextmenu", onContextMenu);
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", cancel, { passive: true });
    el.addEventListener("touchcancel", cancel, { passive: true });
    return () => {
      cancel();
      el.removeEventListener("contextmenu", onContextMenu);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", cancel);
      el.removeEventListener("touchcancel", cancel);
    };
  }, [targetRef]);

  const model = useMemo(
    () => [
      {
        label,
        icon,
        command: () => {
          // Focus the panel FIRST — the spotlight routes the selected symbol
          // by focusedPanelType (grid adds to the watchlist, bookByPrice
          // changes the book symbol).
          const ws = useWorkspaceStore.getState();
          if (ws.focusedPanelType !== panelType) {
            ws.setFocusedPanelType(panelType);
          }
          symbolSpotlightRef.current?.open();
        },
      },
    ],
    [label, icon, panelType],
  );

  if (!isFeatureEnabled("chartContextMenu")) return null;

  return (
    <ContextMenu
      model={model}
      ref={cmRef}
      appendTo={document.body}
      className="chart-context-menu"
      baseZIndex={100000}
      onShow={() =>
        requestAnimationFrame(() =>
          clampMenuToViewport(cmRef.current?.getElement?.()),
        )
      }
    />
  );
};

PanelSymbolContextMenu.propTypes = {
  targetRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  panelType: PropTypes.oneOf(["grid", "bookByPrice"]).isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.string,
};

export default PanelSymbolContextMenu;
